import { useActivityStore } from '@/entities/activity';
import { useAchievementsStore } from '@/entities/achievement';
import { useDerbyStore } from '@/entities/derby';
import { useDrawsStore } from '@/entities/draw';
import { useLeaderboardStore } from '@/entities/leaderboard';
import { useMatchesStore } from '@/entities/match';
import { usePredictionsStore } from '@/entities/prediction';
import { useResultsStore } from '@/entities/result';
import { useRewardTrackStore } from '@/entities/reward-track';
import { useSessionStore } from '@/entities/session';

/**
 * Перезагрузка всего, что могло протухнуть, пока мини-апп был свёрнут.
 *
 * Сводка игрока обновляется всегда: шапка со счётчиками видна на любом
 * экране, и подписка на канал могла появиться как раз в те минуты, что
 * игрок провёл вне приложения.
 *
 * Итоги периодов тоже всегда: неделя могла закрыться, пока игрок был
 * свёрнут.
 *
 * Остальные срезы обновляются, ТОЛЬКО если их уже грузили. Иначе возврат
 * в приложение с главной тянул бы Дерби, Карьеру и ленту матчей, которых
 * игрок в этой сессии не открывал — десяток запросов вместо трёх.
 */
const MIN_INTERVAL_MS = 15_000;

let lastRefreshAt = 0;
let inFlight: Promise<void> | null = null;

export function refreshAppData(now = Date.now()): Promise<void> {
  // Клиент шлёт `activated` и `visibilitychange` подряд — без этого
  // каждый возврат в приложение давал бы двойной комплект запросов
  if (now - lastRefreshAt < MIN_INTERVAL_MS) return Promise.resolve();

  inFlight ??= run().finally(() => {
    inFlight = null;
    lastRefreshAt = Date.now();
  });

  return inFlight;
}

/**
 * `force` стоит везде намеренно: у сторов есть окно свежести, а смысл
 * возврата в приложение ровно в том, что данные протухли.
 */
async function run(): Promise<void> {
  const session = useSessionStore.getState();
  if (!session.accessToken) return;

  const matches = useMatchesStore.getState();
  const predictions = usePredictionsStore.getState();
  const leaderboard = useLeaderboardStore.getState();
  const derby = useDerbyStore.getState();
  const rewardTrack = useRewardTrackStore.getState();
  const activity = useActivityStore.getState();
  const draws = useDrawsStore.getState();
  const results = useResultsStore.getState();

  const summaryRefresh = session.loadSummary({ force: true });
  const stale: Promise<unknown>[] = [summaryRefresh];

  // Подписка на BetBoom не говорит о подписке на Okko. Проверяем обе,
  // независимо от своего канала; сервер ограничивает частоту и исключает
  // уже выполненные партнёрские Ачивки. Остальные данные грузятся параллельно.
  stale.push(
    session.recheckSubscription().then(async () => {
      // До ответа проверки сервер ещё мог не начислить награду. Обновление
      // Ачивок и счётчиков обязано идти ПОСЛЕ неё, иначе остаётся старый экран.
      // Первый запрос может ещё идти: singleFlight иначе присоединит
      // второй к старому ответу, прочитанному до начисления награды.
      await Promise.allSettled([summaryRefresh]);
      const refreshAchievements = async (): Promise<void> => {
        const achievements = useAchievementsStore.getState();
        if (achievements.loadedAt === null && !achievements.isLoading) return;
        // Ачивки тоже singleFlight: сперва завершаем запрос со старым
        // снимком, затем отдельно читаем прогресс после начисления.
        if (achievements.isLoading) await achievements.load({ force: true });
        await achievements.load({ force: true });
      };
      await Promise.allSettled([
        session.loadSummary({ force: true }),
        refreshAchievements(),
      ]);
    }),
  );
  const refreshIfSeen = (
    loadedAt: number | null,
    load: () => Promise<void>,
  ): void => {
    if (loadedAt !== null) stale.push(load());
  };

  refreshIfSeen(matches.loadedAt, () => matches.load({ force: true }));
  refreshIfSeen(matches.finishedLoadedAt, () =>
    matches.loadFinished({ force: true }),
  );
  refreshIfSeen(predictions.loadedAt, () => predictions.load({ force: true }));
  refreshIfSeen(leaderboard.weeklyLoadedAt, () =>
    leaderboard.loadWeekly({ force: true }),
  );
  refreshIfSeen(leaderboard.monthlyLoadedAt, () =>
    leaderboard.loadMonthly({ force: true }),
  );
  refreshIfSeen(derby.loadedAt, () => derby.load({ force: true }));
  refreshIfSeen(rewardTrack.loadedAt, () => rewardTrack.load({ force: true }));
  refreshIfSeen(activity.loadedAt, () => activity.load({ force: true }));
  refreshIfSeen(draws.currentLoadedAt, () =>
    draws.loadCurrent({ force: true }),
  );
  // Итоги периодов — всегда: пока игрок был свёрнут, могла закрыться
  // неделя, и держать поздравление до следующего запуска незачем
  stale.push(results.load({ force: true }));

  await Promise.allSettled(stale);
}

/** Только для тестов: сбросить троттлинг между сценариями. */
export function resetRefreshThrottle(): void {
  lastRefreshAt = 0;
  inFlight = null;
}
